import type { ReactNode } from 'react'
import { Link } from 'react-router-dom'
import type { ISODate, JournalEntry } from '@app/shared'

import type { JournalDayContext, JournalStats } from '../../lib/data'
import { CheckSquare, GraduationCap, OpenBook, Star } from '../icons'
import { ChevronRight, ListLines } from './icons'

// ============================================================================
// THE READ-ONLY "THAT DAY" STRIP — docs/design/handoff/"LifeOS Journal.dc.html",
// option 5b, and the Threads card beneath it.
//
// DELIBERATELY NON-INTERACTIVE (README §2: "editing history from a reading
// surface is a trap"). Nothing in this file is a button, a checkbox or a link
// into another domain — it states what else the system knows about the date and
// stops there. The one exception is Threads, whose "See all" is a link back into
// this same screen.
// ============================================================================

export function ThatDay({
  context,
  error,
  isPending,
}: {
  context?: JournalDayContext
  error: unknown
  isPending: boolean
}) {
  return (
    <section className="flex flex-none flex-col gap-[11px] rounded-context border border-line bg-surface px-[18px] py-[15px] shadow-card">
      <header className="flex items-center gap-[9px]">
        <h2 className="text-[10px] font-semibold uppercase leading-none tracking-[0.08em] text-microcopy">
          That day
        </h2>
        <span className="ml-auto text-[10px] leading-none text-faintest">read-only · auto</span>
      </header>

      {error ? (
        <p className="text-[12px] leading-[1.5] text-microcopy text-pretty">
          {error instanceof Error ? error.message : 'That day’s context could not be read.'}
        </p>
      ) : isPending || !context ? (
        <div className="flex flex-col gap-3">
          <span className="sr-only">Loading…</span>
          {[0, 1, 2].map((i) => (
            <div key={i} className="flex gap-[11px]" aria-hidden>
              <div className="size-4 flex-none rounded-[4px] bg-fill" />
              <div className="flex-1">
                <div className="h-[9px] w-[70px] rounded-full bg-fill" />
                <div className="mt-[7px] h-[9px] w-full rounded-full bg-line-3" />
              </div>
            </div>
          ))}
        </div>
      ) : (
        <Sections context={context} />
      )}
    </section>
  )
}

function Sections({ context }: { context: JournalDayContext }) {
  const blocks: ReactNode[] = []

  if (context.attended.length > 0) {
    blocks.push(
      <Row key="attended" icon={<GraduationCap size={16} className="text-school" />} title="Attended">
        {context.attended.map((a) => (
          <div key={a.title}>
            {a.title}
            {a.detail && ` · ${a.detail}`}
          </div>
        ))}
      </Row>,
    )
  }

  if (context.completedTasks.length > 0) {
    blocks.push(
      <Row
        key="completed"
        icon={<CheckSquare size={16} className="text-tasks" />}
        title="Completed"
        pill={
          <span className="rounded-pill bg-orange-fill-2 px-[7px] py-[2px] text-[9.5px] font-semibold leading-[1.5] text-orange">
            {context.completedTasks.length} {context.completedTasks.length === 1 ? 'task' : 'tasks'}
          </span>
        }
      >
        {context.completedTasks.map((t) => (
          <div key={t}>{t}</div>
        ))}
      </Row>,
    )
  }

  if (context.learning.length > 0) {
    blocks.push(
      <Row key="learning" icon={<OpenBook size={16} className="text-learning" />} title="Learning">
        {context.learning.map((l) => (
          <div key={l.title}>
            {l.title}
            {/* Minutes only when minutes were actually logged — an item finished without a timer
                shows no duration rather than a rounded guess. */}
            {l.minutes !== null && ` · ${l.minutes} min`}
          </div>
        ))}
        {context.streakDay !== null && (
          <div className="mt-[6px] text-[11px] font-medium leading-none text-success-dot">
            Day {context.streakDay} of the streak
          </div>
        )}
      </Row>,
    )
  }

  if (context.saves.length > 0) {
    blocks.push(
      <Row key="saves" icon={<Star size={16} className="text-interests" />} title="Saved to Interests">
        {context.saves.map((s) => (
          <div key={s.title}>
            {s.title}
            {s.detail && ` — ${s.detail}`}
          </div>
        ))}
      </Row>,
    )
  }

  if (blocks.length === 0) {
    return (
      <p className="text-[12.5px] leading-[1.55] text-ink-3 text-pretty">
        Nothing else was recorded on this date — no class, no completed task, no learning item and
        no save. The entry above is all there is.
      </p>
    )
  }

  return (
    <>
      {blocks.map((block, i) => (
        <div key={i} className="contents">
          {i > 0 && <div className="h-px bg-fill" aria-hidden />}
          {block}
        </div>
      ))}
    </>
  )
}

function Row({
  icon,
  title,
  pill,
  children,
}: {
  icon: ReactNode
  title: string
  pill?: ReactNode
  children: ReactNode
}) {
  return (
    <div className="flex items-start gap-[11px]">
      <span className="mt-px flex-none">{icon}</span>
      <div className="flex-1">
        <div className="flex items-center gap-[7px]">
          <h3 className="text-[11.5px] font-semibold leading-none text-ink-2">{title}</h3>
          {pill}
        </div>
        <div className="mt-[5px] text-[12.5px] leading-[1.55] text-ink-3 text-pretty">{children}</div>
      </div>
    </div>
  )
}

// ── Threads ─────────────────────────────────────────────────────────────────

/**
 * The tags on THIS entry, each with how many entries carry it across the whole archive — the
 * thread the day belongs to. Counts come from the same stats read the index uses, so the number
 * here and the number on the filter chip can never disagree.
 */
export function Threads({
  entry,
  stats,
  statsError,
}: {
  entry: JournalEntry
  stats?: JournalStats
  statsError: unknown
}) {
  if (entry.tags.length === 0) {
    return (
      <section className="flex flex-none flex-col gap-[11px] rounded-context border border-line bg-surface px-[18px] py-4 shadow-card">
        <header className="flex items-center gap-[9px]">
          <ListLines size={16} className="text-muted" />
          <h2 className="text-[12.5px] font-semibold leading-none">Threads</h2>
        </header>
        <p className="text-[12.5px] leading-[1.55] text-ink-3 text-pretty">
          This day carries no tags, so it is not part of a thread yet.
        </p>
      </section>
    )
  }

  const countOf = (tag: string) => stats?.tags.find((t) => t.tag === tag)?.count ?? null
  const biggest = [...entry.tags].sort((a, b) => (countOf(b) ?? 0) - (countOf(a) ?? 0))[0]!

  return (
    <section className="flex flex-none flex-col gap-[11px] rounded-context border border-line bg-surface px-[18px] py-4 shadow-card">
      <header className="flex items-center gap-[9px]">
        <ListLines size={16} className="text-muted" />
        <h2 className="text-[12.5px] font-semibold leading-none">Threads</h2>
      </header>

      <div className="flex flex-col gap-[9px]">
        {entry.tags.map((tag) => {
          const count = countOf(tag)
          return (
            <div key={tag} className="flex items-center gap-[9px]">
              <span className="rounded-pill border border-line-3 bg-surface-4 px-[9px] py-1 text-[11px] font-medium leading-none text-ink-2">
                #{tag}
              </span>
              <span className="text-[11.5px] leading-none text-muted">
                {/* No count rather than a made-up one when the archive read is unavailable. */}
                {count === null
                  ? statsError
                    ? 'count unavailable'
                    : 'counting…'
                  : `${count} ${count === 1 ? 'entry' : 'entries'}`}
              </span>
              <Link
                to={`/journal?tag=${encodeURIComponent(tag)}`}
                className="ml-auto text-[11px] font-medium leading-none text-success-dot transition-colors duration-[var(--dur-state)] ease-[var(--ease-ink)] hover:text-success"
              >
                See all
              </Link>
            </div>
          )
        })}
      </div>

      {countOf(biggest) !== null && (
        <>
          <div className="h-px bg-fill" aria-hidden />
          <p className="text-[12.5px] leading-[1.55] text-ink-3 text-pretty">
            <span className="text-ink-2">#{biggest}</span> runs through{' '}
            <span className="text-ink-2">
              {countOf(biggest)} {countOf(biggest) === 1 ? 'entry' : 'entries'}
            </span>{' '}
            in the archive.
          </p>
        </>
      )}
    </section>
  )
}

// ── The next-day footer ─────────────────────────────────────────────────────

export function NextDayCard({ date, label }: { date: ISODate; label: string }) {
  return (
    <Link
      to={`/journal/${date}`}
      className="mt-auto flex flex-none items-center gap-3 rounded-context border border-line-3 bg-surface-4 px-[18px] py-[14px] transition-colors duration-[var(--dur-fast)] ease-[var(--ease-out)] hover:bg-fill"
    >
      <span className="text-[10px] font-semibold uppercase leading-none tracking-[0.08em] text-microcopy">
        Next day
      </span>
      <span className="text-[12px] font-medium leading-[1.3] text-ink-2">{label}</span>
      <ChevronRight size={14} className="ml-auto text-muted" />
    </Link>
  )
}
